import { site } from "@/config/site";

type OrderCompleteProps = {
  productLabel: string;
  quantity: number;
  productPrice: number;
  shipping: number;
  total: number;
  customerName: string;
};

export default function OrderComplete({
  productLabel,
  quantity,
  productPrice,
  shipping,
  total,
  customerName,
}: OrderCompleteProps) {
  return (
    <div className="space-y-6">
      {/* 1. ご注文ありがとうございます */}
      <section className="bg-accent-100 border border-accent-300 rounded-lg p-6 md:p-8 text-center">
        <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-500 text-background-50 mb-4">
          <i className="ri-checkbox-circle-line text-2xl"></i>
        </div>
        <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
          ご注文ありがとうございます
        </h2>
        <p className="mt-3 text-sm md:text-base text-foreground-700 max-w-xl mx-auto leading-relaxed">
          {customerName
            ? `${customerName} 様、この度はご注文いただき誠にありがとうございます。`
            : "この度はご注文いただき誠にありがとうございます。"}
          <br />
          ご注文内容をご確認のうえ、合計金額をお振込みください。
          <br />
          ご入金を確認後、商品の発送手続きを行います。
        </p>
      </section>

      {/* 2. ご注文内容 */}
      <section className="bg-background-100 rounded-lg border border-background-200 p-5 md:p-6">
        <h3 className="text-base font-heading font-bold text-foreground-950 mb-4 flex items-center gap-2">
          <i className="ri-receipt-line text-accent-700"></i>
          ご注文内容
        </h3>
        <dl className="space-y-3 text-sm md:text-base">
          <div className="flex justify-between gap-3">
            <dt className="text-foreground-500 shrink-0">注文商品</dt>
            <dd className="text-foreground-900 text-right">{productLabel}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-foreground-500 shrink-0">数量</dt>
            <dd className="text-foreground-900">{quantity}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-foreground-500 shrink-0">商品価格</dt>
            <dd className="text-foreground-900">{productPrice.toLocaleString()}円</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-foreground-500 shrink-0">送料</dt>
            <dd className="text-foreground-900">{shipping.toLocaleString()}円</dd>
          </div>
        </dl>
      </section>

      {/* 3. お支払い合計金額（大きく目立たせる） */}
      <section className="bg-primary-500 rounded-lg p-6 md:p-8 text-center">
        <p className="text-sm md:text-base font-bold text-background-50 mb-2">お支払い合計金額</p>
        <p className="font-heading font-bold text-4xl md:text-5xl text-background-50">
          {total.toLocaleString()}
          <span className="text-xl md:text-2xl ml-1">円</span>
        </p>
        <p className="mt-3 text-xs md:text-sm text-background-50/90">
          上記の金額をお振込みください
        </p>
      </section>

      {/* 4. お支払いについて */}
      <section className="bg-background-100 rounded-lg border border-background-200 p-5 md:p-6">
        <h3 className="text-base font-heading font-bold text-foreground-950 mb-4 flex items-center gap-2">
          <i className="ri-bank-card-line text-accent-700"></i>
          お支払いについて
        </h3>
        <div className="space-y-2 text-sm md:text-base text-foreground-700">
          <p className="font-bold text-foreground-950">お支払い方法：銀行振込のみ</p>
          <p>ご注文内容をご確認のうえ、合計金額をお振込みください。</p>
          <p>ご入金を確認後、商品の発送手続きを行います。</p>
        </div>
      </section>

      {/* 5. お振込先 */}
      <section className="bg-background-100 rounded-lg border border-background-200 p-5 md:p-6">
        <h3 className="text-base font-heading font-bold text-foreground-950 mb-4 flex items-center gap-2">
          <i className="ri-bank-line text-accent-700"></i>
          お振込先
        </h3>
        <div className="bg-background-50 rounded-md border border-background-200 p-4 space-y-1 text-sm text-foreground-700">
          <p><span className="font-bold">銀行名：</span>{site.bankName}</p>
          <p><span className="font-bold">支店名：</span>{site.bankBranch}</p>
          <p><span className="font-bold">口座種別：</span>{site.bankAccountType}</p>
          <p><span className="font-bold">口座番号：</span>{site.bankAccountNumber}</p>
          <p><span className="font-bold">口座名義：</span>{site.bankAccountHolder}</p>
        </div>
      </section>

      {/* 6. 発送について */}
      <section className="bg-background-100 rounded-lg border border-background-200 p-5 md:p-6">
        <h3 className="text-base font-heading font-bold text-foreground-950 mb-4 flex items-center gap-2">
          <i className="ri-truck-line text-accent-700"></i>
          発送について
        </h3>
        <div className="space-y-2 text-sm md:text-base text-foreground-700">
          <p>入金確認後、商品を発送いたします。</p>
          <p>在庫がある場合は、入金確認後に速やかに発送いたします。</p>
          <p>在庫がない場合は、ご登録いただいた電話番号へご連絡いたします。</p>
        </div>
      </section>
    </div>
  );
}